import { useState } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { type LanguageCode } from '@kairelo/shared';
import { ToastProvider } from './components/ui.tsx';
import { I18nProvider, useT } from './i18n/I18n.tsx';
import { LanguageSelect } from './components/LanguageSelect.tsx';
import { loadVisitorLanguage, saveVisitorLanguage } from './lib/visitorLanguage.js';
import { Icon } from './components/Icon.tsx';
import { Logo } from './components/Logo.tsx';
import { Landing } from './routes/Landing.tsx';
import { Login } from './routes/Login.tsx';
import { ConfirmLogin } from './routes/ConfirmLogin.tsx';
import { OwnerLayout } from './routes/OwnerLayout.tsx';
import { Messenger } from './routes/Messenger.tsx';
import { ProfileEditor } from './routes/ProfileEditor.tsx';
import { BrandingEditor } from './routes/BrandingEditor.tsx';
import { Settings } from './routes/Settings.tsx';
import { Join } from './routes/Join.tsx';
import { VisitorConversation } from './routes/VisitorConversation.tsx';

/** Scroll container for routes outside the owner application shell. */
function Shell({ children }: { children: React.ReactNode }) {
  return <div className="shell">{children}</div>;
}

/**
 * Shell plus the Kairelo masthead, for the pages that are ours to brand.
 *
 * Deliberately not used on the visitor's routes. A visitor arrives holding one
 * person's link, and what they need to decide about is that person — not us. A
 * "Sign in" link there is worse than noise: it offers an account to someone the
 * product's whole premise says does not need one, on a page whose job is to say
 * so. The conversation itself takes no chrome at all.
 *
 * These pages are localised from the browser's own language. They were the last
 * ones that were not: the catalogues already carried every `login.*` and
 * `confirm.*` string, but the routes sat outside the provider and rendered
 * hardcoded English, so a Dutch browser was told the product was multilingual
 * in English.
 *
 * The choice is stored per browser under the same key the visitor routes use.
 * Someone who picked Croatian on a join page should not be handed English when
 * they follow the logo back to the front page — it is one person and one
 * browser, whichever door they came through.
 */
function MarketingShell({
  children,
  signIn = false,
}: {
  children: React.ReactNode;
  /** Only where signing in is not already what the page is for. */
  signIn?: boolean;
}) {
  const [language, setLanguage] = useState<LanguageCode>(loadVisitorLanguage);

  return (
    <I18nProvider language={language}>
      <Shell>
        {/* Full-bleed and sticky, with only its contents on the page's measure. */}
        <header className="masthead">
          <div className="masthead__inner">
            <Link to="/" aria-label="Kairelo">
              <Logo size={28} />
            </Link>
            <MastheadLinks
              signIn={signIn}
              language={language}
              onLanguage={(next) => {
                setLanguage(next);
                saveVisitorLanguage(next);
              }}
            />
          </div>
        </header>
        {children}
      </Shell>
    </I18nProvider>
  );
}

/**
 * Sits inside the provider so its own label is in the language being chosen,
 * and so the picker re-labels itself the moment the choice changes.
 *
 * Detection is a guess — a shared machine, a work laptop set to the employer's
 * language, a browser installed in whatever the shop had. The guess is worth
 * making and it is not worth trusting, so it is always correctable, and the
 * correction sticks.
 */
function MastheadLinks({
  signIn,
  language,
  onLanguage,
}: {
  signIn: boolean;
  language: LanguageCode;
  onLanguage: (language: LanguageCode) => void;
}) {
  const t = useT();
  return (
    <nav className="masthead__links">
      <label className="masthead__language">
        <span className="visually-hidden">{t('landing.languageLabel')}</span>
        <LanguageSelect value={language} onChange={onLanguage} />
      </label>
      {signIn ? <Link to="/login">{t('login.title')}</Link> : null}
    </nav>
  );
}

export function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route
          path="/"
          element={
            <MarketingShell signIn>
              <Landing />
            </MarketingShell>
          }
        />
        <Route
          path="/login"
          element={
            <MarketingShell>
              <Login />
            </MarketingShell>
          }
        />
        <Route
          path="/auth/confirm"
          element={
            <MarketingShell>
              <ConfirmLogin />
            </MarketingShell>
          }
        />

        {/*
          The messenger is its own full-screen surface: a two-pane layout on a
          wide screen, one pane at a time on a phone. It deliberately sits
          outside the settings chrome.
        */}
        <Route path="/app" element={<Navigate to="/app/conversations" replace />} />
        <Route path="/app/conversations" element={<Messenger />} />
        <Route path="/app/conversations/:id" element={<Messenger />} />

        {/* Settings keep ordinary application chrome. */}
        <Route path="/app" element={<OwnerLayout />}>
          <Route path="profile" element={<ProfileEditor />} />
          <Route path="branding" element={<BrandingEditor />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Visitor. The invitation path carries the bearer token; the
            conversation key travels only in the fragment. */}
        <Route
          path="/join/:token"
          element={
            <Shell>
              <Join />
            </Shell>
          }
        />
        <Route
          path="/chat/:id"
          element={
            <Shell>
              <VisitorConversation />
            </Shell>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </ToastProvider>
  );
}

function NotFound() {
  return (
    <I18nProvider language={loadVisitorLanguage()}>
      <div className="shell">
        <div className="page page--narrow">
          <NotFoundBody />
        </div>
      </div>
    </I18nProvider>
  );
}

function NotFoundBody() {
  const t = useT();
  return (
    <div className="empty">
      <span className="empty__icon">
        <Icon name="info" size={22} />
      </span>
      <h3>{t('error.notFound')}</h3>
      <p>{t('error.notFoundBody')}</p>
    </div>
  );
}
